import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  fetchPublishedScholars,
  fetchMyScholar,
  filterScholars,
  sortScholars,
  deriveScholarFacets,
  EMPTY_SCHOLAR_FILTERS,
} from '../lib/scholars.js';
import { useAuth } from '../context/AuthContext.jsx';
import { loadWithRetry } from '../lib/loadWithRetry.js';
import ScholarCard from '../components/scholars/ScholarCard.jsx';
import ScholarFilters from '../components/scholars/ScholarFilters.jsx';
import ScholarDetailModal from '../components/scholars/ScholarDetailModal.jsx';

export default function Scholars() {
  const { t } = useTranslation();
  const { isActive, user } = useAuth();
  const [scholars, setScholars] = useState(null);
  const [myScholar, setMyScholar] = useState(null);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState(EMPTY_SCHOLAR_FILTERS);
  const [sort, setSort] = useState('name');
  const [detail, setDetail] = useState(null);
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setScholars(null);
    setError(null);
    loadWithRetry(fetchPublishedScholars, { label: 'scholars' })
      .then((rows) => !cancelled && setScholars(rows))
      .catch((err) => !cancelled && setError(err.message ?? String(err)));
    return () => {
      cancelled = true;
    };
  }, [retryKey]);

  useEffect(() => {
    if (!user?.id) {
      setMyScholar(null);
      return undefined;
    }
    let cancelled = false;
    fetchMyScholar(user.id)
      .then((row) => !cancelled && setMyScholar(row))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user?.id, scholars]);

  const facets = useMemo(() => (scholars ? deriveScholarFacets(scholars) : null), [scholars]);

  const results = useMemo(() => {
    if (!scholars) return [];
    return sortScholars(filterScholars(scholars, { ...filters, query }), sort);
  }, [scholars, filters, query, sort]);

  const activeCount = (filters.country ? 1 : 0) + (filters.university ? 1 : 0);

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-8 pb-20 pt-32 text-center">
        <h1 className="font-serif text-3xl text-gold-light">{t('common.error')}</h1>
        <p className="mt-3 text-ink-secondary">{error}</p>
        <button
          type="button"
          className="btn-primary mt-6"
          onClick={() => setRetryKey((k) => k + 1)}
        >
          {t('common.tryAgain')}
        </button>
      </div>
    );
  }

  if (!scholars || !facets) {
    return (
      <div role="status" aria-live="polite" className="mx-auto max-w-3xl px-8 pb-20 pt-32 text-center text-ink-secondary">
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-shell px-6 pb-20 pt-28">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="section-label">{t('home.tiles.scholars.title')}</div>
          <h1 className="font-serif text-4xl font-semibold text-cream">
            {t('scholars.heading')}
          </h1>
          <p className="mt-2 max-w-2xl text-ink-secondary">
            {t('scholars.subheading', { count: scholars.length })}
          </p>
        </div>
        {isActive && (
          <div className="flex flex-wrap gap-3">
            {myScholar ? (
              <Link to={`/scholars/${myScholar.id}/edit`} className="btn-ghost">
                {t('scholars.editMine')}
              </Link>
            ) : (
              <Link to="/scholars/new?self=1" className="btn-primary">
                {t('scholars.addMine')}
              </Link>
            )}
            <Link to="/scholars/new" className="btn-ghost">
              {t('scholars.addOther')}
            </Link>
          </div>
        )}
      </header>

      <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-navy-base/50 p-3 backdrop-blur">
        <input
          type="search"
          placeholder={t('scholars.searchPlaceholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="min-w-0 flex-1 rounded-md border border-white/10 bg-navy-deep/60 px-4 py-2 text-cream placeholder:text-ink-muted focus:border-gold/50"
        />
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="rounded-md border border-white/10 bg-navy-deep/60 px-3 py-2 text-sm text-cream focus:border-gold/50"
        >
          <option value="name">{t('scholars.sort.name')}</option>
          <option value="university">{t('scholars.sort.university')}</option>
          <option value="country">{t('scholars.sort.country')}</option>
        </select>
        <button
          type="button"
          className="btn-ghost text-sm md:hidden"
          onClick={() => setShowFiltersMobile((s) => !s)}
        >
          {t('common.filter')}
          {activeCount > 0 && (
            <span className="ml-1 rounded-full bg-gold/20 px-1.5 text-xs text-gold-light">
              {activeCount}
            </span>
          )}
        </button>
      </div>

      <p className="mb-4 text-sm text-ink-secondary">
        {t('scholars.resultCount', { count: results.length })}
      </p>

      <div className="grid gap-8 md:grid-cols-[260px_1fr]">
        <div className={`md:block ${showFiltersMobile ? 'block' : 'hidden'}`}>
          <ScholarFilters
            facets={facets}
            filters={filters}
            onChange={setFilters}
            onClear={() => setFilters(EMPTY_SCHOLAR_FILTERS)}
            activeCount={activeCount}
          />
        </div>

        <div>
          {results.length === 0 ? (
            <p className="rounded-lg border border-white/10 bg-navy-base/40 p-10 text-center text-ink-secondary">
              {scholars.length === 0 ? t('scholars.emptyAll') : t('scholars.empty')}
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {results.map((s) => (
                <ScholarCard
                  key={s.id}
                  scholar={s}
                  onOpen={setDetail}
                  isMine={!!user && s.profile_id === user.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <ScholarDetailModal
        scholar={detail}
        onClose={() => setDetail(null)}
        onDeleted={(id) => {
          setScholars((prev) => prev?.filter((s) => s.id !== id) ?? prev);
        }}
      />
    </div>
  );
}
